import type { CreditStatus } from '../../../services/creditsApi';

/**
 * Ler o saldo de créditos — e nunca confundir "zerado" com "não sei".
 *
 * MEDIDO 2026-09-07: três superfícies faziam `creditStatus?.currentCredits ??
 * 0`. Enquanto `GET /credits/status` está em voo (ou falhou), o popover do
 * cabeçalho renderizava **"0 de 0 · 0% restante"** com a barra vazia — que
 * qualquer pessoa lê como "sua conta acabou", não como "ainda estou
 * carregando". Zero fabricado é pior que campo ausente: ele se lê como
 * medição.
 *
 * É a QUINTA vez nesta semana que o mesmo zero aparece (preço de crédito,
 * consentimento LGPD, campos de dinheiro ×100, preço negociado do contrato,
 * e agora o saldo). Por isso a leitura virou um lugar só: três cópias da
 * mesma pergunta divergem na primeira edição de uma.
 */
export interface LeituraDeSaldo {
  /** `false` = a resposta não chegou (em voo, erro, payload sem os campos). */
  conhecido: boolean;
  /** `null` quando `conhecido` é `false` — nunca 0. */
  atual: number | null;
  total: number | null;
  /** Percentual restante; `null` sem leitura E sem denominador. */
  pct: number | null;
}

const numero = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

/**
 * `undefined` (em voo/erro) e payload sem os campos caem no MESMO desfecho —
 * os dois são "não sei", e distingui-los na tela não muda nada para quem lê.
 */
export function lerSaldo(status: CreditStatus | undefined | null): LeituraDeSaldo {
  const atual = numero(status?.currentCredits);
  const total = numero(status?.totalCredits);
  if (atual === null) return { conhecido: false, atual: null, total: null, pct: null };
  // `pctRemaining` vem do backend; só cai no cálculo local quando ele não veio.
  const doBackend = numero(status?.pctRemaining);
  const pct =
    doBackend !== null
      ? Math.max(0, Math.min(100, Math.round(doBackend)))
      : total !== null && total > 0
        ? Math.max(0, Math.min(100, Math.round((atual / total) * 100)))
        : null;
  return { conhecido: true, atual, total, pct };
}

/**
 * Três respostas, e as três importam.
 *
 * `nao_sei` NÃO é `nao`: gerar um briefing é uma ESCRITA PAGA, então recusar
 * na dúvida é a decisão certa (o custo do erro seria uma cobrança que ninguém
 * viu) — mas quem vê o botão apagado precisa ler POR QUÊ. Antes, a ignorância
 * e o saldo insuficiente produziam o mesmo botão morto, sem uma palavra.
 */
export function podeGastar(leitura: LeituraDeSaldo, custo: number): 'sim' | 'nao' | 'nao_sei' {
  if (!leitura.conhecido || leitura.atual === null) return 'nao_sei';
  return leitura.atual >= custo ? 'sim' : 'nao';
}

/** O número, ou um traço — nunca um zero que ninguém mediu. */
export function textoDeSaldo(valor: number | null): string {
  return valor === null ? '—' : String(valor);
}
